import { Inject, Injectable } from '@angular/core';

import { Observable, of, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { Config, MW_APP_CONFIG } from '../../config';
import { MwHttpService } from '../../http/services/http.service';
import { MwProgressBarService } from '../../services/progress-bar.service';
import { CompanyViewModel, UserProfileViewModel } from '../models';
import { LanguageChangeRequest } from '../models/language-change.model';
import { NotificationTokenRegisterViewModel } from '../models/notification-token-view.model';
import { MwAuthService } from './auth.service';

@Injectable()
export class MwUserApiService {
  static nextId = 0;
  id: string = `${
    this.config.environment.clientId
  }-${MwUserApiService.nextId++}`;

  constructor(
    private readonly http: MwHttpService,
    private readonly progressBarService: MwProgressBarService,
    private readonly authService: MwAuthService,
    @Inject(MW_APP_CONFIG) private config: Config
  ) {}

  getCompanies(companyId?: number): Observable<CompanyViewModel[]> {
    const localCompanyId = companyId || 0;
    this.progressBarService.show();
    const query =
      localCompanyId > 0 ? `?$filter=CompanyId eq ${companyId}` : '';
    return this.http
      .authGet<CompanyViewModel[]>('/v2/users/companies' + query, {
        ignoreNotifyStatusCodes: [401, 403],
      })
      .pipe(tap(() => this.progressBarService.hide()));
  }

  getCompany(companyId: number): Observable<CompanyViewModel | null> {
    return companyId > 0
      ? this.http
          .authGet<CompanyViewModel[]>(
            `/v2/users/companies?$filter=CompanyId eq ${companyId}`
          )
          .pipe(map((companies) => companies[0] || null))
      : of(null);
  }

  getProfile(companyId?: number): Observable<UserProfileViewModel> {
    const url = `/v2/users/profile`;
    return this.http
      .authPost<UserProfileViewModel>(url, {
        ignoreNotifyStatusCodes: [405, 403],
        body: { actions: [] },
        companyId: companyId || 0,
      })
      .pipe(catchError(() => of(new UserProfileViewModel())));
  }

  registerFcmToken(
    token: string
  ): Observable<NotificationTokenRegisterViewModel> {
    return this.http.authPost<NotificationTokenRegisterViewModel>(
      `/notification/v1/Companies/{companyId}/register`,
      {
        body: { token },
        ignoreNotifyStatusCodes: [404],
      }
    );
  }

  hasAccess(actions: string[]): Observable<Record<string, boolean>> {
    return this.http
      .authPost<Record<string, boolean>>(`/user/access`, {
        body: actions,
        ignoreNotifyStatusCodes: [404],
      })
      .pipe(catchError(() => this.hasAccessFallback(actions)));
  }

  hasLinkWithMobileWorkerAccount(): Observable<boolean> {
    const hasLinkWithMobileWorkerAccount$ = this.http
      .authGet<boolean>(`/v2/users/login/hasLinkWithMobileWorker`, {
        ignoreNotifyStatusCodes: [404, 403],
      })
      .pipe(catchError(() => of(true)));

    return this.authService
      .isDevincoConnectAccessToken()
      .pipe(
        switchMap((isDevincoConnectAccessToken) =>
          isDevincoConnectAccessToken
            ? hasLinkWithMobileWorkerAccount$
            : of(true)
        )
      );
  }

  hasAccessFallback(actions: string[]): Observable<Record<string, boolean>> {
    const result = this.http
      .authPost<{ actions: string[] }>(`/v2/users/profile`, {
        body: { actions },
      })
      .pipe(
        map((data) =>
          actions.reduce(
            (prev, curr) => ({
              ...prev,
              [curr]: data.actions.findIndex((t) => t === curr) !== -1,
            }),
            {}
          )
        )
      );
    return result;
  }

  changeLanguage(language: string): Observable<LanguageChangeRequest> {
    const url = `/integration/v1/Companies/{companyId}/Users/preference/language`;
    return this.http.authPost<LanguageChangeRequest>(url, {
      body: { language },
    });
  }
}
